import * as Haptics from 'expo-haptics';
import i18n from '@/utils/i18n';
import React, { useCallback, useEffect } from 'react';
import { APP_ROUTES, PAYMENT_ROUTES } from '@/navigators/routes';
import { BaseActionSheet } from './base-action-sheet';
import { Button } from './button';
import { CloseButton } from './close-button';
import { FONTS, SCREEN_PADDING, TYPOGRAPHY } from '@/styles';
import {
  getPlacedOrderCopy,
  OrderSubmittedComponentProps,
} from '@/utils/order-submitted';
import { HappyBox } from './happy-box';
import { MultiStyleText } from './multi-style-text';
import { PaymentsStackParams } from '@/navigators/types';
import { Platform, StyleSheet, Text, View } from 'react-native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useNavigation } from '@react-navigation/native';
import { useToasts } from '@/contexts/toast-context';
import { useUserContext } from '@/contexts/user-context';

export type OrderSubmittedActionSheetProps = OrderSubmittedComponentProps & {
  onClose: () => void;
};

export const OrderSubmittedComponent: React.FC<
  OrderSubmittedComponentProps
> = ({
  canPayInvoice,
  externalReviewRequired,
  isShippingOrder,
  isSplitOrder,
  fulfillmentDates,
  loyaltyEarns,
  onPayNowPress,
  reviewRequired,
  unpaidInvoice,
}) => {
  const { loyaltyEarnedMessage, orderFulfillmentMessage, orderTitle } =
    getPlacedOrderCopy({
      externalReviewRequired,
      fulfillmentDates,
      isShippingOrder,
      isSplitOrder,
      loyaltyEarns,
      reviewRequired,
    });

  const makePaymentMessage = canPayInvoice
    ? i18n.t('order_placed_action_sheet.pay_for_order')
    : null;

  return (
    <View>
      <HappyBox showConfetti={!externalReviewRequired && !reviewRequired} />
      <Text style={[TYPOGRAPHY.h3, styles.titleText]}>{orderTitle}</Text>
      {orderFulfillmentMessage || makePaymentMessage ? (
        <Text style={[TYPOGRAPHY.actionSheetMessageText, styles.messageText]}>
          <Text>{orderFulfillmentMessage}</Text>
          {orderFulfillmentMessage && makePaymentMessage ? ' ' : null}
          <Text>{makePaymentMessage}</Text>
        </Text>
      ) : null}
      {loyaltyEarnedMessage ? (
        <MultiStyleText
          defaultStyle={[
            TYPOGRAPHY.actionSheetMessageText,
            styles.loyaltyMessage,
          ]}
          emphasisStyle={FONTS.primaryBold}
        >
          {loyaltyEarnedMessage}
        </MultiStyleText>
      ) : null}
      {canPayInvoice && onPayNowPress && unpaidInvoice ? (
        <Button
          onPress={onPayNowPress}
          style={styles.buttonMargin}
          trackingData={{
            name: 'order_submitted_action_sheet.pay_now_button',
            properties: {
              invoiceUUID: unpaidInvoice.uuid,
            },
          }}
          type="primary"
        >
          {i18n.t('pay_now')}
        </Button>
      ) : null}
    </View>
  );
};

export const OrderSubmittedActionSheet: React.FC<
  OrderSubmittedActionSheetProps
> = ({ onClose, completionToast, ...rest }) => {
  const { navigate } =
    useNavigation<StackNavigationProp<PaymentsStackParams>>();
  const {
    selectedRestaurantRelationship: {
      appPaymentsEnabled,
      employeeRole,
      showPaymentsTab,
    },
  } = useUserContext();
  const { showToast } = useToasts();

  const canClose = !rest.externalReviewRequired;
  const canPayInvoice = Boolean(
    rest.unpaidInvoice && showPaymentsTab && appPaymentsEnabled && employeeRole,
  );

  useEffect(() => {
    if (Platform.OS !== 'web') {
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
    }
  }, []);

  const closeActionSheet = useCallback(() => {
    if (completionToast) {
      showToast(completionToast);
    }

    onClose();
  }, [completionToast, onClose, showToast]);

  return (
    <BaseActionSheet
      contentStyle={styles.content}
      isVisible
      onClose={() => {
        if (canClose) {
          closeActionSheet();
        }
      }}
    >
      {canClose && !canPayInvoice ? (
        <View style={styles.closeButtonContainer}>
          <CloseButton
            onPress={onClose}
            testID="order-submitted-modal-close-button"
            trackingData={{
              name: 'order_placed_action_sheet.close_button',
            }}
          />
        </View>
      ) : null}
      <OrderSubmittedComponent
        {...rest}
        canPayInvoice={canPayInvoice}
        onPayNowPress={() => {
          navigate(APP_ROUTES.Payments, {
            params: {
              unpaidInvoice: rest.unpaidInvoice,
            },
            screen: PAYMENT_ROUTES.Base,
          });
          closeActionSheet();
        }}
      />
    </BaseActionSheet>
  );
};

const styles = StyleSheet.create({
  buttonMargin: {
    marginTop: 16,
  },
  closeButtonContainer: {
    position: 'absolute',
    right: SCREEN_PADDING,
    top: SCREEN_PADDING,
  },
  content: {
    paddingHorizontal: 48,
    paddingTop: 24,
  },
  loyaltyMessage: {
    marginTop: 8,
    textAlign: 'center',
  },
  messageText: {
    textAlign: 'center',
  },
  titleText: {
    marginBottom: 8,
    textAlign: 'center',
  },
});
